import { DOCUMENT_TYPE_LABELS, type Document } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { DocumentFormDialog } from '~/features/documents/DocumentFormDialog.js';
import { useDeleteDocument, useDocumentsList } from '~/features/documents/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/_auth/documents')({
  component: DocumentsPage,
});

function DocumentsPage() {
  const { data, isLoading, error, refetch } = useDocumentsList();
  const del = useDeleteDocument();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Document | undefined>();
  const [deleting, setDeleting] = useState<Document | undefined>();

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.slug);
      toast.success('Documento removido.');
      setDeleting(undefined);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao remover.');
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ O site</p>
        <h1 className="page-title">Documentos</h1>
        <p className="page-sub">
          Os PDFs disponíveis para download na página de Transparência. Use "Subir nova versão" para
          trocar um arquivo sem perder o registro — o endereço do documento continua o mesmo. A
          ordem define a posição na página; menor número aparece primeiro.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <p style={{ color: 'var(--fg-muted)' }}>
              {data ? `${data.length} ${data.length === 1 ? 'documento' : 'documentos'}` : '—'}
            </p>
            <Button variant="primary" onClick={() => setCreating(true)}>
              Novo documento
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="dt__state">
            <Spinner size="lg" />
          </div>
        )}

        {error && (
          <div className="dt__state">
            <p className="dt__state-title">Não foi possível carregar.</p>
            <p className="dt__state-sub">{error.displayMessage}</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {data && data.length === 0 && (
          <div className="dt__state">
            <p className="dt__state-title">Nenhum documento cadastrado.</p>
            <Button variant="primary" onClick={() => setCreating(true)}>
              Cadastrar primeiro documento
            </Button>
          </div>
        )}

        {data && data.length > 0 && (
          <div className="document-list">
            {data.map((doc) => (
              <article key={doc.slug} className="document-card">
                <header className="document-card__head">
                  <div>
                    <Badge tone="primary">{DOCUMENT_TYPE_LABELS[doc.tipo]}</Badge>
                    {!doc.visivel && <Badge tone="muted">oculto</Badge>}
                    {doc.versao && <Badge tone="neutral">{doc.versao}</Badge>}
                  </div>
                </header>
                <h2 className="document-card__title">{doc.titulo}</h2>
                {doc.descricao && <p className="document-card__desc">{doc.descricao}</p>}
                <dl className="document-card__meta">
                  <div>
                    <dt>Arquivo</dt>
                    <dd>
                      <a href={doc.arquivo.url} target="_blank" rel="noopener noreferrer">
                        Abrir PDF →
                      </a>
                      <span style={{ color: 'var(--fg-muted)', marginLeft: 'var(--s-2)' }}>
                        ({formatBytes(doc.arquivo.tamanho)})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt>Endereço</dt>
                    <dd>
                      <code>{doc.slug}</code>
                    </dd>
                  </div>
                  {doc.revisadoEm && (
                    <div>
                      <dt>Última revisão</dt>
                      <dd>{new Date(doc.revisadoEm).toLocaleDateString('pt-BR')}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Ordem</dt>
                    <dd>{doc.ordem}</dd>
                  </div>
                </dl>
                <footer className="document-card__actions">
                  <Button variant="secondary" size="sm" onClick={() => setEditing(doc)}>
                    Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(doc)}>
                    Excluir
                  </Button>
                </footer>
              </article>
            ))}
          </div>
        )}
      </section>

      <DocumentFormDialog open={creating} onClose={() => setCreating(false)} />
      <DocumentFormDialog
        open={!!editing}
        document={editing}
        onClose={() => setEditing(undefined)}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(undefined)}
        onConfirm={handleDelete}
        title="Excluir documento?"
        description={`"${deleting?.titulo}" será removido em definitivo e sairá da página de Transparência. Se a intenção é apenas tirá-lo do ar por ora, desmarque "Visível" na edição em vez de excluir.`}
        confirmLabel="Excluir"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
